import { useState, useCallback, useEffect } from "react";
import useFetch from "./useFetch";
import { getTokenFromUrl, buildCustomerSearchUrl } from "../utils/helpers";
import config from "../config";

const useCustomers = () => {
  const [state, setState] = useState({
    customers: [],
    total: 0,
    offset: 0,
    term: "",
    selectedCustomer: null,
    showModal: false,
  });

  const { data, loading, error, fetchData } = useFetch();

  const searchCustomers = useCallback(
    async (searchTerm = "", offset = 0) => {
      const token = getTokenFromUrl();
      if (!token) {
        throw new Error("No token provided");
      }

      const url = buildCustomerSearchUrl(
        config.url_customer,
        token,
        searchTerm,
        offset
      );

      try {
        const response = await fetchData(url);
        setState((prev) => ({
          ...prev,
          customers: response.customers || [],
          total: response.total || 0,
          term: searchTerm,
          offset: offset,
        }));
        return response;
      } catch (error) {
        setState((prev) => ({
          ...prev,
          customers: [],
          total: 0,
        }));
        throw error;
      }
    },
    [fetchData]
  );

  const handleSearch = useCallback(
    async (term) => {
      setState((prev) => ({ ...prev, offset: 0, term }));
      try {
        await searchCustomers(term, 0);
      } catch (error) {
        setState((prev) => ({
          ...prev,
          customers: [],
          total: 0,
        }));
      }
    },
    [searchCustomers]
  );

  const nextPage = useCallback(async () => {
    const newOffset = state.offset + 1;
    try {
      await searchCustomers(state.term, newOffset);
    } catch (error) {
      // Handle error silently
    }
  }, [searchCustomers, state.offset, state.term]);

  const previousPage = useCallback(async () => {
    if (state.offset === 0) return;
    const newOffset = state.offset - 1;
    try {
      await searchCustomers(state.term, newOffset);
    } catch (error) {
      // Handle error silently
    }
  }, [searchCustomers, state.offset, state.term]);

  const openModal = useCallback((customerId) => {
    setState((prev) => {
      const customer = prev.customers.find((c) => c.id === customerId);
      if (customer) {
        return {
          ...prev,
          selectedCustomer: customer,
          showModal: true,
        };
      }
      return prev;
    });
  }, []);

  const closeModal = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedCustomer: null,
      showModal: false,
    }));
  }, []);

  // Initial load
  useEffect(() => {
    const token = getTokenFromUrl();
    if (token) {
      searchCustomers("", 0).catch(() => {
        // Handle error silently
      });
    }
  }, [searchCustomers]);

  return {
    // Data
    customers: state.customers,
    total: state.total,
    offset: state.offset,
    term: state.term,
    selectedCustomer: state.selectedCustomer,
    showModal: state.showModal,

    // Loading and error states
    loading,
    error,

    // Actions
    handleSearch,
    nextPage,
    previousPage,
    openModal,
    closeModal,

    // Computed values
    canGoNext: state.customers.length >= 100,
    canGoPrevious: state.offset > 0,
    currentPage: state.offset + 1,
    hasToken: !!getTokenFromUrl(),
  };
};

export default useCustomers;
